import type {
    SetDataOptions,
    UseQueryOptions,
    UseQueryReturnType
} from '@tanstack/vue-query'

import { useQuery } from '@tanstack/vue-query'
import type { Updater } from '@tanstack/vue-query'
import { createBaseQuery } from './createBaseQuery'
import type {
    AdditionalCreateQueryOptions,
    AdditionalQueryHookOptions,
    ExposeMethods,
    QueryKitKey,
} from 'Common/types/utils/network/querykit/queryTypes'

interface CreateQueryOptions<TFnData, TVariables, Error>
    extends Omit<
        UseQueryOptions<TFnData, Error, unknown, QueryKitKey<TVariables>>,
        'queryKey' | 'queryFn' | 'enabled' | 'select'
    >,
    AdditionalCreateQueryOptions<TFnData, TVariables> { }

type QueryHookOptions<TFnData, Error, TData, TVariables> = Omit<
    UseQueryOptions<TFnData, Error, TData, QueryKitKey<TVariables>>,
    'queryKey' | 'queryFn' | 'enabled'
> &
    AdditionalQueryHookOptions<TFnData, TVariables>

export interface QueryHook<TFnData, TVariables = void, Error = unknown>
    extends ExposeMethods<TFnData, TVariables> {
    <TData = TFnData>(
        options: TVariables extends void
            ? QueryHookOptions<TFnData, Error, TData, TVariables> | void
            : QueryHookOptions<TFnData, Error, TData, TVariables>
    ): UseQueryReturnType<TData, Error> & {
        queryKey: QueryKitKey<TVariables>
        setData: (
            updater: Updater<TFnData | undefined, TFnData>,
            options?: SetDataOptions | undefined
        ) => TFnData | undefined
    }
}

export function createQuery<TFnData, TVariables = void, Error = unknown>(
    options: CreateQueryOptions<TFnData, TVariables, Error>
): QueryHook<TFnData, TVariables, Error> {
    return createBaseQuery(options, useQuery) as QueryHook<
        TFnData,
        TVariables,
        Error
    >
}
